import Papa from 'papaparse';
import * as XLSX from 'xlsx';

/**
 * Parse a file and return structured data
 * Supports CSV, XLSX, XLS formats
 */
export const parseFile = async (file) => {
  const extension = file.name.split('.').pop().toLowerCase();

  switch (extension) {
    case 'csv':
      return parseCSV(file);
    case 'xlsx':
    case 'xls':
      return parseExcel(file);
    default:
      throw new Error(`Unsupported file format: ${extension}`);
  }
};

/**
 * Parse CSV file
 */
export const parseCSV = (file) => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      transformHeader: (header) => header.trim(),
      complete: (results) => {
        resolve({
          data: results.data,
          columns: results.meta.fields || [],
          rowCount: results.data.length,
          errors: results.errors
        });
      },
      error: (error) => {
        reject(error);
      }
    });
  });
};

/**
 * Parse Excel file (XLSX/XLS)
 */
export const parseExcel = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Get first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: ''
        });

        if (jsonData.length === 0) {
          resolve({ data: [], columns: [], rowCount: 0, errors: [] });
          return;
        }

        // First row is headers
        const headers = jsonData[0].map(h => String(h).trim());
        const rows = jsonData.slice(1).map(row => {
          const obj = {};
          headers.forEach((header, index) => {
            obj[header] = row[index] !== undefined ? row[index] : '';
          });
          return obj;
        });

        resolve({
          data: rows,
          columns: headers,
          rowCount: rows.length,
          sheetNames: workbook.SheetNames,
          errors: []
        });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Detect voter data fields from column names
 */
export const detectVoterFields = (columns) => {
  // Use .* to allow any characters (including spaces) between words
  const fieldPatterns = {
    name: /^(name|voter.*name|full.*name)$/i,
    firstName: /^(first.*name|fname|f_name|given.*name)$/i,
    lastName: /^(last.*name|lname|l_name|surname|family.*name)$/i,
    house: /^(house|house.*num|house.*number|street.*num|street.*number)$/i,
    street: /^(street|street.*name|street.*address|road|avenue|addr)$/i,
    address: /^(address|full.*address)$/i,
    city: /^(city|town|municipality)$/i,
    state: /^(state|st|province)$/i,
    zip: /^(zip|zip.*code|postal|postal.*code)$/i,
    phone: /^(phone|telephone|tel|mobile|cell|phone.*number)$/i,
    email: /^(email|e.*mail|email.*address)$/i,
    party: /^(party|party.*affiliation|pol.*party|political.*party|par)$/i,
    age: /^(age|voter.*age)$/i,
    dob: /^(dob|date.*of.*birth|birth.*date|birthday)$/i,
    sex: /^(sex|gender)$/i,
    previousVotes: /^(prev.*votes|previous.*votes|vote.*count|pv|voting.*history)$/i,
    registrationDate: /^(reg.*date|registration.*date|registered)$/i,
    precinct: /^(precinct|voting.*precinct|district)$/i,
    county: /^(county)$/i,
    notes: /^(notes|comments|remarks)$/i
  };

  const detectedFields = [];
  const mapping = {};

  columns.forEach(column => {
    for (const [field, pattern] of Object.entries(fieldPatterns)) {
      if (pattern.test(column)) {
        console.log(`[fileParser] Column "${column}" matched field "${field}" with pattern ${pattern}`);
        detectedFields.push({
          name: field,
          label: formatFieldLabel(field),
          suggested: column,
          description: getFieldDescription(field)
        });
        mapping[field] = column;
        break;
      }
    }
  });

  console.log('[fileParser] Final mapping:', mapping);
  console.log('[fileParser] Detected fields:', detectedFields.map(f => f.name));

  return { detectedFields, mapping };
};

/**
 * Format field name to human-readable label
 */
const formatFieldLabel = (field) => {
  const labels = {
    name: 'Voter Name',
    firstName: 'First Name',
    lastName: 'Last Name',
    house: 'House Number',
    street: 'Street Name',
    address: 'Full Address',
    city: 'City',
    state: 'State',
    zip: 'ZIP Code',
    phone: 'Phone',
    email: 'Email',
    party: 'Party Affiliation',
    age: 'Age',
    dob: 'Date of Birth',
    sex: 'Sex/Gender',
    previousVotes: 'Previous Votes',
    registrationDate: 'Registration Date',
    precinct: 'Precinct',
    county: 'County',
    notes: 'Notes'
  };

  return labels[field] || field;
};

/**
 * Get field description
 */
const getFieldDescription = (field) => {
  const descriptions = {
    name: 'Full name of the voter',
    firstName: 'First/given name',
    lastName: 'Last/family name',
    house: 'House or building number',
    street: 'Street name (e.g., Main St)',
    address: 'Full street address',
    city: 'City or town',
    state: 'State or province',
    zip: 'ZIP or postal code',
    phone: 'Phone number',
    email: 'Email address',
    party: 'Political party affiliation (R, D, I, etc.)',
    age: 'Voter age in years',
    dob: 'Date of birth',
    sex: 'Sex/gender (M/F)',
    previousVotes: 'Number of previous elections voted in',
    registrationDate: 'Voter registration date',
    precinct: 'Voting precinct or district',
    county: 'County name',
    notes: 'Additional notes or comments'
  };

  return descriptions[field] || '';
};

/**
 * Normalize party affiliation codes
 */
export const normalizeParty = (value) => {
  if (!value) return null;

  const normalized = String(value).toUpperCase().trim();

  // Map common variations to standard codes
  const partyMap = {
    'R': 'R', 'REP': 'R', 'REPUBLICAN': 'R', 'GOP': 'R',
    'D': 'D', 'DEM': 'D', 'DEMOCRAT': 'D', 'DEMOCRATIC': 'D',
    'I': 'I', 'IND': 'I', 'INDEPENDENT': 'I', 'NPA': 'I', 'NO PARTY': 'I',
    'L': 'L', 'LIB': 'L', 'LIBERTARIAN': 'L',
    'G': 'G', 'GRN': 'G', 'GREEN': 'G',
    'S': 'S', 'SV': 'S', 'SWING': 'S', 'SWING VOTER': 'S'
  };

  return partyMap[normalized] || 'O'; // 'O' for Other
};

/**
 * Normalize sex/gender codes
 */
export const normalizeSex = (value) => {
  if (!value) return null;

  const normalized = String(value).toUpperCase().trim();

  const sexMap = {
    'M': 'M', 'MALE': 'M', 'MAN': 'M',
    'F': 'F', 'FEMALE': 'F', 'WOMAN': 'F', 'W': 'F'
  };

  return sexMap[normalized] || null;
};

/**
 * Get sample data for preview
 */
export const getSampleData = (data, sampleSize = 5) => {
  return data.slice(0, sampleSize);
};

/**
 * Calculate field statistics
 */
export const calculateFieldStats = (data, field) => {
  const values = data.map(row => row[field]).filter(v => v !== null && v !== undefined && v !== '');

  const distribution = {};
  values.forEach(v => {
    const key = String(v);
    distribution[key] = (distribution[key] || 0) + 1;
  });

  return {
    total: data.length,
    populated: values.length,
    empty: data.length - values.length,
    unique: Object.keys(distribution).length,
    distribution
  };
};
